import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ShieldCheck, Mail, User, Lock, RefreshCw, 
  Smartphone, ArrowRight, ArrowLeft, Gift, Zap
} from "lucide-react";
import axios from "axios";
import SEO from '../components/SEO';

export default function Auth() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("login");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({ 
    name: "", email: "", phone: "", otp: "", password: "",
    referredBy: "",
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const refCode = queryParams.get("ref");
    if (refCode) {
      setFormData(prev => ({ ...prev, referredBy: refCode }));
      setMode("signup"); 
    }
  }, [location]);

  useEffect(() => {
    if (user) {
      user.isBusiness ? navigate("/business/home") : navigate("/");
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanEmail = formData.email.trim().toLowerCase();
      if (mode === "login") {
        const result = await login(cleanEmail, formData.password);
        if (result.success) {
          // Success handled by context
        } else {
          alert("Access Denied: " + result.message);
        }
      } 
      else if (mode === "signup" && step === 1) {
        await axios.post("https://wethrift-backend.onrender.com/api/auth/send-otp-signup", { 
          name: formData.name,
          email: cleanEmail,
          phone: formData.phone,
          password: formData.password,
          referredBy: formData.referredBy 
        });
        setStep(2);
      }
      else if (mode === "signup" && step === 2) {
        const response = await axios.post("https://wethrift-backend.onrender.com/api/auth/verify-otp-combined", {
          email: cleanEmail,
          otp: formData.otp
        });
        if (response.data.accessToken) {
          localStorage.setItem("weThriftUser", JSON.stringify(response.data));
          window.location.reload(); 
        }
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Modern UI Classes
  const inputContainer = "relative group mb-4";
  const inputIcon = "absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-emerald-400 transition-colors duration-300";
  const inputClass = `w-full bg-zinc-900/50 border border-white/5 p-4 pl-12 rounded-2xl text-[11px] font-bold uppercase tracking-widest text-white outline-none transition-all duration-300 focus:border-emerald-500/50 focus:bg-zinc-800/80 focus:ring-4 focus:ring-emerald-500/10 shadow-inner`;

  return (
    <>
      <SEO title="Access Terminal | Join the Archive" url="/auth" />

      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12 relative overflow-hidden">
        {/* Animated Background Gradients */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-900/20 blur-[120px] rounded-full animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-zinc-900/40 blur-[120px] rounded-full"></div>

        <div className="w-full max-w-lg relative">
          {/* Card Wrapper with Glassmorphism */}
          <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/5 p-10 md:p-14 rounded-[3.5rem] shadow-2xl relative overflow-hidden">
            
            {/* Top Branding */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[9px] font-black tracking-[0.2em] uppercase mb-6">
                <Zap size={10} fill="currentColor" /> System Online
              </div>
              <h1 className="text-6xl font-[1000] italic text-white uppercase tracking-tighter leading-none mb-2">
                WE <span className="text-emerald-500">THRIFT</span>
              </h1>
              <p className="text-[10px] tracking-[0.5em] text-zinc-500 uppercase font-black opacity-60 italic">Archive_Access_v2.5</p>
            </div>

            {/* Mode Toggle */}
            {step === 1 && (
              <div className="flex gap-2 mb-8 bg-black/40 p-1.5 rounded-2xl border border-white/5 shadow-inner">
                <button 
                  type="button" 
                  onClick={() => setMode("login")} 
                  className={`flex-1 py-4 rounded-xl text-[10px] font-black uppercase transition-all duration-500 ${mode === "login" ? "bg-white text-black scale-[1.02] shadow-xl shadow-white/5" : "text-zinc-500 hover:text-zinc-300"}`}
                >
                  Log_In
                </button>
                <button 
                  type="button" 
                  onClick={() => setMode("signup")} 
                  className={`flex-1 py-4 rounded-xl text-[10px] font-black uppercase transition-all duration-500 ${mode === "signup" ? "bg-white text-black scale-[1.02] shadow-xl shadow-white/5" : "text-zinc-500 hover:text-zinc-300"}`}
                >
                  Register
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-2">
              {step === 1 ? (
                <div className="animate-in slide-in-from-bottom-4 duration-700">
                  {mode === "signup" && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className={inputContainer}>
                          <User className={inputIcon} size={16} />
                          <input name="name" required placeholder="Display Name" className={inputClass} value={formData.name} onChange={handleChange} />
                        </div>
                        <div className={inputContainer}>
                          <Smartphone className={inputIcon} size={16} />
                          <input name="phone" required placeholder="Phone_ID" className={inputClass} value={formData.phone} onChange={handleChange} />
                        </div>
                      </div>
                      <div className={inputContainer}>
                        <Gift className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500/50" size={16} />
                        <input name="referredBy" value={formData.referredBy} placeholder="Invite Code (Optional)" className={`${inputClass} !border-emerald-500/20 text-emerald-400 placeholder:text-emerald-900/50`} onChange={handleChange} />
                      </div>
                    </div>
                  )}

                  <div className={inputContainer}>
                    <Mail className={inputIcon} size={16} />
                    <input name="email" type="email" required placeholder="Email_Network" className={inputClass} value={formData.email} onChange={handleChange} />
                  </div>
                  <div className={inputContainer}>
                    <Lock className={inputIcon} size={16} />
                    <input name="password" type="password" required placeholder="Encryption_Key" className={inputClass} value={formData.password} onChange={handleChange} />
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 animate-in zoom-in duration-500">
                  <div className="w-24 h-24 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-emerald-500/5">
                    <ShieldCheck className="text-emerald-500" size={40} />
                  </div>
                  <p className="text-zinc-400 text-[10px] mb-2 uppercase font-black tracking-[0.3em]">Protocol Verification</p>
                  <p className="text-zinc-600 text-[9px] mb-8 font-medium">System sent code to {formData.email}</p>
                  <input 
                    name="otp" 
                    required 
                    maxLength="6" 
                    placeholder="------" 
                    className="bg-transparent text-center text-6xl font-black tracking-[0.2em] text-white outline-none w-full mb-10 placeholder:text-zinc-800" 
                    onChange={handleChange} 
                    autoFocus 
                  />
                  <button type="button" onClick={() => setStep(1)} className="text-[9px] font-black text-zinc-600 uppercase tracking-widest hover:text-white transition-colors flex items-center justify-center gap-2 mx-auto">
                    <ArrowLeft size={12} /> Incorrect Email?
                  </button>
                </div>
              )}

              {/* Dynamic Action Button */}
              <button 
                type="submit" 
                disabled={loading} 
                className="group w-full relative overflow-hidden py-5 rounded-[1.8rem] font-black uppercase tracking-[0.3em] text-[11px] transition-all duration-500 bg-white text-black hover:bg-emerald-500 hover:text-white hover:scale-[1.02] shadow-2xl shadow-white/5 active:scale-95 disabled:opacity-50"
              >
                <div className="relative z-10 flex items-center justify-center gap-3">
                  {loading ? (
                    <RefreshCw className="animate-spin" size={18} />
                  ) : (
                    <>
                      {step === 2 ? "Decrypt_Account" : mode === "signup" ? "Initialize_Signup" : "Establish_Connection"} 
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                    </>
                  )}
                </div>
              </button>
            </form>
          </div>

          {/* Footer Info */}
          <div className="mt-8 flex justify-between items-center px-8">
            <p className="text-[8px] font-black text-zinc-700 uppercase tracking-widest">© we_thrift_intl_2026</p>
            <div className="h-[1px] flex-1 bg-zinc-900 mx-6"></div>
            <p className="text-[8px] font-black text-zinc-700 uppercase tracking-widest">Secured_Vault_V2</p>
          </div>
        </div>
      </div>
    </>
  );
}